<template>
  <VCard v-if="!element.id" class="drag-handle pa-2 elevation-0">
    Drag me to add text
  </VCard>
  <div
    v-else
    :class="['text-elem-wrapper', { hovering: isHovering }]"
    @mouseenter="onMouseEnter"
    @mousemove="onMouseMove"
    @mouseleave="onMouseLeave"
  >
    <div
      :class="['drag-handle force-ems text-elem', { editing: showingEditUI }]"
      :style="{ 'font-size': `${myFontSize}em` }"
      @click.prevent="toggleEditing()"
    >
      <BaseEditable
        :key="pendingContent.tag"
        :content="element.content.text"
        :mods="textMod"
        :tag="tagWithClasses"
        :class="element.content.class"
        @update="updatedText"
        @dirtyUpdate="$emit('dirtyUpdate', $event)"
        @onBlur="onEditorBlur"
      />
    </div>
    <LayoutElementSettingsMenu
      v-if="isShowingLayoutEditUI"
      v-model="settingsOpen"
      :confirming-delete="confirmingDelete"
      @delete="onDelete"
      @done="turnOffEditing"
    >
      <div class="d-flex align-center mt-2">
        <VSlider
          :value="pendingContent.size"
          min=".5"
          max="4"
          step="0.05"
          label="Size"
          hide-details
          color="grey darken-2"
          class="mr-3 mt-0"
          @input="pendingContent.size = $event"
          @change="changeSize"
        />
        <input
          :value="pendingContent.size"
          type="number"
          min=".5"
          max="4"
          step="0.05"
          @change="onSizeInput"
        />
      </div>
      <div class="d-flex align-center mt-2">
        <span class="body grey--text text--darken-1 mr-3">Tag</span>

        <VSelect
          :value="pendingContent.tag"
          :items="tagOptions"
          hide-details
          dense
          class="pt-0"
          @change="changeTag"
        />
      </div>
    </LayoutElementSettingsMenu>
  </div>
</template>

<script>
import { mapMutations } from 'vuex'
import { MOD_ENTITY, REMOVE_ALL_ENTITY_MODS } from '@constants/mutations'
import DebouncedSave from '@mixins/debounced-save'
import LayoutElementEditing from '@mixins/layout-element-editing'
import LayoutElementSettingsMenu from './LayoutElementSettingsMenu'

const MIN_SIZE = 0.5
const MAX_SIZE = 4
const SIZE_STEPS_PER_UNIT = 20

// The slider's keys can go past either end and off the step
const toSavedSize = (val) => {
  const size = parseFloat(val)
  if (isNaN(size)) return null
  const clamped = Math.max(MIN_SIZE, Math.min(MAX_SIZE, size))
  return Math.round(clamped * SIZE_STEPS_PER_UNIT) / SIZE_STEPS_PER_UNIT
}

export default {
  name: 'LayoutElementTypeText',
  components: { LayoutElementSettingsMenu },
  mixins: [DebouncedSave, LayoutElementEditing],
  props: {
    element: {
      type: Object,
      required: true,
      default: () => {},
    },
    ratio: {
      type: Number,
      default: 1,
    },
    fontSize: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      pendingContent: {
        // A stored size can be a string, and the slider's keys would concatenate it
        size: parseFloat(this.element.content.size) || 1,
        tag: this.element.content.tag || 'div',
      },
      tagOptions: ['div', 'h2', 'h1'],
    }
  },
  computed: {
    textMod() {
      const mods = this.$store.getters['entities/getEntityMods'](
        'layoutElements',
        this.element.id
      )
      return mods ? mods.text : null
    },
    myFontSize() {
      return this.fontSize * (this.pendingContent.size / 10) * this.ratio
    },
    showingEditUI() {
      return this.isEditing && this.isShowingLayoutEditUI
    },
    tagWithClasses() {
      const tagClass = this.element.content['tag-class'] || ''
      let els = [this.pendingContent.tag]
      if (Array.isArray(tagClass)) {
        tagClass.forEach((cls) => cls.split(' ').forEach((c) => els.push(c)))
      } else if (tagClass.length) {
        els = [...els, ...(tagClass.trim().length ? tagClass.split(' ') : [])]
      }
      els.push('pm-el')
      return els.join('.')
    },
  },
  watch: {
    // A size or tag saved elsewhere only updates the display. Saving it from
    // here would make every polling TV try to write it back.
    'element.content.size'(val) {
      const size = parseFloat(val)
      if (size && size !== this.pendingContent.size) {
        this.pendingContent.size = size
      }
    },
    'element.content.tag'(val) {
      if (val && val !== this.pendingContent.tag) {
        this.pendingContent.tag = val
      }
    },
  },
  methods: {
    ...mapMutations('entities', [MOD_ENTITY, REMOVE_ALL_ENTITY_MODS]),
    toggleEditing() {
      if (!this.isShowingLayoutEditUI) return
      this.isEditing = true
    },
    updatedText(text) {
      // Typed back to the published text: nothing left to publish
      if (text === this.element.content.text) {
        this[REMOVE_ALL_ENTITY_MODS]({
          entityName: 'layoutElements',
          entityId: this.element.id,
        })
        return
      }
      this[MOD_ENTITY]({
        entityName: 'layoutElements',
        entityId: this.element.id,
        modKey: 'text',
        modVal: text,
      })
      this[MOD_ENTITY]({
        entityName: 'layoutElements',
        entityId: this.element.id,
        modKey: 'title',
        modVal: text || '',
      })
    },
    onEditorBlur() {
      this.isEditing = false
    },
    // Only an owner's input saves the size and tag: the slider's change (its
    // input event also fires on prop changes), the number field or the select.
    changeSize(val) {
      const size = toSavedSize(val)
      if (size === null) return
      this.pendingContent.size = size
      this.scheduleSave('element', this.updateSizeAndTag)
    },
    changeTag(val) {
      this.pendingContent.tag = val
      this.scheduleSave('element', this.updateSizeAndTag)
    },
    onSizeInput(e) {
      const val = parseFloat(e.target.value)
      if (!isNaN(val)) {
        this.changeSize(val)
      } else {
        e.target.value = this.pendingContent.size
      }
    },
    updateSizeAndTag() {
      this.updateElement({
        id: this.element.id,
        type: 'text',
        title: this.element.content.text || this.element.title,
        content: {
          ...this.element.content,
          size: this.pendingContent.size,
          tag: this.pendingContent.tag,
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.text-elem-wrapper {
  position: relative;
}
.text-elem-wrapper.hovering {
  ::v-deep .elem-settings-btn {
    opacity: 1;
  }
}
::v-deep .v-slider__thumb-container {
  display: flex;
}
::v-deep .v-slider__thumb {
  width: 16px;
  height: 16px;
  left: -8px;
}
.text-elem {
  padding: 0.2em 0.4em;
}
</style>
